// src/pages/CustomContent.js — Admin ▸ Custom Content (route /custom-content)
//
// Every "Request Custom Material" request. New requests (from the pop-up
// form, functions/customMaterial.js) keep their details under user / meta /
// content and are marked paid by Stripe; the older static forms wrote flat
// fields. Both are read here through one `view()` so the table works for all.

import React, { useState, useEffect, useMemo } from "react";
import { getFirestore, collection, getDocs, doc, updateDoc, deleteDoc, arrayUnion, serverTimestamp } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import "./AdminRoles.css";

const STATUSES = ["awaiting_payment", "paid", "in_progress", "done", "submitted"];
const STATUS_LABEL = {
  awaiting_payment: "Awaiting payment",
  submitted: "Submitted (old form)",
  paid: "Paid — to do",
  in_progress: "In progress",
  done: "Done",
};

const toDate = (v) => (v?.toDate ? v.toDate() : v ? new Date(v) : null);

/** one shape for old (flat) and new (nested) requests */
function view(c) {
  const u = c.user || {};
  const m = c.meta || {};
  const k = c.content || {};
  const questions = k.questions || (k.qa || []).map((q) => ({ question: q.question || q.q || "", answer: q.answer || q.correct || "", wrong: [] }));
  return {
    id: c.id,
    name: c.name || [u.sirName || c.sirName, u.firstName || c.firstName, u.lastName || c.lastName].filter(Boolean).join(" "),
    email: c.email || u.email || "",
    topic: m.topic || c.topic || "",
    grade: m.grade || c.grade || "",
    state: String(m.state || c.state || "").toUpperCase(),
    subject: m.subject || c.subject || "",
    style: m.style || c.style || "",
    status: c.status || m.status || (c.paid ? "paid" : "submitted"),
    paid: c.paid === true,
    created: toDate(m.createdAt || c.createdAt),
    facts: k.facts || [],
    questions,
    pairs: k.pairs || k.dragDropPairs || [],
    notesFromTeacher: k.notes || "",
    notes: c.notes || [],
    raw: c,
  };
}

const CustomContent = () => {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: "created", direction: "desc" });
  const [filter, setFilter] = useState("open");
  const [noteText, setNoteText] = useState("");
  const [file, setFile] = useState(null);
  const navigate = useNavigate();
  const db = getFirestore();

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, "customContent"));
        setRows(snap.docs.map((d) => view({ id: d.id, ...d.data() })));
      } catch (error) {
        console.error("Error fetching data:", error.message);
      }
    })();
  }, [db]);

  const shown = useMemo(() => {
    const list = rows.filter((r) =>
      filter === "all" ? true : filter === "open" ? r.paid && r.status !== "done" : filter === "unpaid" ? !r.paid : r.status === "done"
    );
    const { key, direction } = sortConfig;
    const dir = direction === "asc" ? 1 : -1;
    return [...list].sort((a, b) => {
      const x = key === "created" ? a.created?.getTime() || 0 : String(a[key] ?? "").toLowerCase();
      const y = key === "created" ? b.created?.getTime() || 0 : String(b[key] ?? "").toLowerCase();
      return x < y ? -dir : x > y ? dir : 0;
    });
  }, [rows, filter, sortConfig]);

  const patch = (id, change) => setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...change } : r)));

  const handleSort = (key) =>
    setSortConfig((c) => ({ key, direction: c.key === key && c.direction === "asc" ? "desc" : "asc" }));

  const togglePaid = async (r) => {
    try {
      await updateDoc(doc(db, "customContent", r.id), { paid: !r.paid, status: !r.paid ? "paid" : "awaiting_payment", updatedAt: serverTimestamp() });
      patch(r.id, { paid: !r.paid, status: !r.paid ? "paid" : "awaiting_payment" });
    } catch (error) {
      console.error("Error updating paid status:", error.message);
      alert("Failed to update paid status. Please try again.");
    }
  };

  const setStatus = async (r, status) => {
    try {
      await updateDoc(doc(db, "customContent", r.id), { status, "meta.status": status, updatedAt: serverTimestamp() });
      patch(r.id, { status });
      if (open?.id === r.id) setOpen({ ...open, status });
    } catch (error) {
      console.error("Error updating status:", error.message);
      alert("Failed to update status. Please try again.");
    }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete the request "${r.topic || r.id}"? This can't be undone.`)) return;
    try {
      await deleteDoc(doc(db, "customContent", r.id));
      setRows((prev) => prev.filter((x) => x.id !== r.id));
      setOpen(null);
    } catch (error) {
      console.error("Error deleting content:", error.message);
      alert("Failed to delete content. Please try again.");
    }
  };

  const handleAddNote = async (r) => {
    try {
      const note = { text: noteText, fileUrl: null };
      if (file) {
        const fileRef = ref(getStorage(), `notes/${r.id}/${file.name}`);
        await uploadBytes(fileRef, file);
        note.fileUrl = await getDownloadURL(fileRef);
      }
      await updateDoc(doc(db, "customContent", r.id), { notes: arrayUnion(note) });
      const notes = [...(r.notes || []), note];
      patch(r.id, { notes });
      setOpen({ ...r, notes });
      setNoteText("");
      setFile(null);
    } catch (error) {
      console.error("Error adding note:", error.message);
      alert("Failed to add note. Please try again.");
    }
  };

  const cols = [
    ["created", "Date"],
    ["name", "Name"],
    ["email", "Email"],
    ["topic", "Topic"],
    ["subject", "Subject"],
    ["grade", "Grade"],
    ["state", "State"],
    ["style", "Style"],
    ["status", "Status"],
  ];

  return (
    <div className="container">
      <button onClick={() => navigate("/admin")} className="return-button">
        Return to Admin Dashboard
      </button>
      <h1>Admin Dashboard: Custom Content</h1>

      <div className="section">
        <h2>Custom material requests</h2>
        <p>
          Teachers send these from <b>Request Custom Material</b> on their portal ($29 each, subscribers only). A request is
          marked paid automatically when Stripe confirms the payment.
        </p>
        <p>
          Show:{" "}
          {[
            ["open", "Paid — to do"],
            ["unpaid", "Not paid"],
            ["done", "Done"],
            ["all", "All"],
          ].map(([v, l]) => (
            <button key={v} type="button" className={`sort-button ${filter === v ? "is-on" : ""}`} style={{ fontWeight: filter === v ? 800 : 400, marginRight: 6 }} onClick={() => setFilter(v)}>
              {l}
            </button>
          ))}
        </p>
        <div className="bordered-container">
          <table className="excel-table">
            <thead>
              <tr>
                {cols.map(([key, label]) => (
                  <th key={key}>
                    {label}{" "}
                    <button className="sort-button" onClick={() => handleSort(key)}>
                      {sortConfig.key === key ? (sortConfig.direction === "asc" ? "↑" : "↓") : "⇅"}
                    </button>
                  </th>
                ))}
                <th>Paid</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 && (
                <tr>
                  <td colSpan={cols.length + 2}>Nothing here.</td>
                </tr>
              )}
              {shown.map((r) => (
                <tr key={r.id} style={{ backgroundColor: r.status === "done" ? "#eef2ff" : r.paid ? "#d4edda" : "transparent" }}>
                  <td>{r.created ? r.created.toLocaleDateString() : ""}</td>
                  <td>{r.name}</td>
                  <td>{r.email}</td>
                  <td>{r.topic}</td>
                  <td>{r.subject}</td>
                  <td>{r.grade}</td>
                  <td>{r.state}</td>
                  <td>{r.style}</td>
                  <td>
                    <select value={STATUSES.includes(r.status) ? r.status : "submitted"} onChange={(e) => setStatus(r, e.target.value)}>
                      {STATUSES.map((st) => (
                        <option key={st} value={st}>
                          {STATUS_LABEL[st]}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <button onClick={() => togglePaid(r)} className={`toggle-button ${r.paid ? "paid" : "not-paid"}`}>
                      {r.paid ? "Paid" : "Not Paid"}
                    </button>
                  </td>
                  <td>
                    <button onClick={() => setOpen(r)} className="details-button">
                      Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fullscreen-modal">
          <div className="fullscreen-content">
            <h2 className="center-text">{open.topic || "Custom material request"}</h2>
            <div className="modal-content-scroll">
              <p>
                <b>{open.name}</b> · {open.email}
                <br />
                {open.subject} · grade {open.grade} · {open.state} · style: <b>{open.style || "Any"}</b>
                <br />
                {STATUS_LABEL[open.status] || open.status} · {open.paid ? "paid" : "not paid"} · id {open.id}
              </p>

              {open.facts.length > 0 && (
                <>
                  <h3>Facts ({open.facts.length})</h3>
                  <ol>
                    {open.facts.map((f, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <li key={i}>{f}</li>
                    ))}
                  </ol>
                </>
              )}

              {open.questions.length > 0 && (
                <>
                  <h3>Questions ({open.questions.length})</h3>
                  <ol>
                    {open.questions.map((q, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <li key={i}>
                        {q.question} — <b>✓ {q.answer}</b>
                        {q.wrong?.length ? <span> (wrong: {q.wrong.join(", ")})</span> : null}
                      </li>
                    ))}
                  </ol>
                </>
              )}

              {open.pairs.length > 0 && (
                <>
                  <h3>Matching pairs ({open.pairs.length})</h3>
                  <ul>
                    {open.pairs.map((p, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <li key={i}>
                        {p.left || p.drag || p.term} ↔ {p.right || p.drop || p.match}
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {open.notesFromTeacher && (
                <>
                  <h3>From the teacher</h3>
                  <p>{open.notesFromTeacher}</p>
                </>
              )}

              {!open.facts.length && !open.questions.length && (
                <pre style={{ whiteSpace: "pre-wrap", fontSize: 12 }}>{JSON.stringify(open.raw.content || open.raw, null, 2)}</pre>
              )}

              {open.notes?.length > 0 && (
                <>
                  <h3>Admin notes</h3>
                  <ul>
                    {open.notes.map((note, index) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <li key={index}>
                        <p>{note.text}</p>
                        {note.fileUrl && (
                          <a href={note.fileUrl} target="_blank" rel="noopener noreferrer">
                            View File
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
            <div className="button-group">
              <button onClick={() => handleDelete(open)} className="red-button">
                Delete
              </button>
              <button onClick={() => setOpen(null)} className="azul-button">
                Close
              </button>
            </div>
            <div className="add-note-section">
              <h3>Add Note</h3>
              <textarea placeholder="Enter your note here" value={noteText} onChange={(e) => setNoteText(e.target.value)} className="textarea" />
              <input type="file" onChange={(e) => setFile(e.target.files[0])} className="file-input" />
              <div className="button-group">
                <button onClick={() => handleAddNote(open)} className="green-button">
                  Save
                </button>
                <button
                  onClick={() => {
                    setNoteText("");
                    setFile(null);
                  }}
                  className="red-button"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomContent;
